import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    RefreshCcw,
    LogOut,
    Eye,
    Users,
    TrendingUp,
    CircleDollarSign,
    ShoppingBag,
    Compass,
    Heart,
    Download,
    Mail,
    MailCheck,
    MailOpen,
    MousePointerClick,
    Globe,
    Smartphone,
    Monitor,
    Activity,
} from "lucide-react";
import { toast } from "sonner";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    BarChart,
    Bar,
} from "recharts";
import SEO from "@/components/common/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const RANGES = [
    { days: 7, label: "7d" },
    { days: 30, label: "30d" },
    { days: 90, label: "90d" },
];

export default function AdminAnalytics() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [series, setSeries] = useState([]);
    const [funnel, setFunnel] = useState([]);
    const [top, setTop] = useState({ pages: [], referrers: [], devices: [] });
    const [recent, setRecent] = useState([]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, ts, fn, tp, ev] = await Promise.all([
                api.get(`/admin/analytics/overview?days=${days}`),
                api.get(`/admin/analytics/timeseries?days=${days}`),
                api.get(`/admin/analytics/funnel?days=${days}`),
                api.get(`/admin/analytics/top-pages?days=${days}`),
                api.get(`/admin/analytics/events?limit=30`),
            ]);
            setOverview(ov.data);
            setSeries(ts.data.series || []);
            setFunnel(fn.data.steps || []);
            setTop(tp.data || { pages: [], referrers: [], devices: [] });
            setRecent(ev.data.items || []);
        } catch {
            toast.error("No se pudieron cargar las métricas.");
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { reload(); }, [reload]);

    async function handleLogout() {
        await logout();
        navigate("/login");
    }

    const maxFunnel = Math.max(...funnel.map((s) => s.count || 0), 1);

    return (
        <>
            <SEO title="Admin · Analytics" description="Métricas y monitoring del ecosistema" path="/admin/analytics" />
            <section className="py-12 md:py-16" data-testid="admin-analytics">
                <div className="container-x">
                    {/* Header */}
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-10 pb-8 border-b border-[var(--line)]">
                        <div>
                            <Link
                                to="/admin"
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] hover:text-[var(--danger)] mb-4"
                                data-testid="analytics-back-to-hub"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Volver al panel
                            </Link>
                            <p className="eyebrow mb-3">Departamento · Monitoring</p>
                            <h1 className="font-display text-4xl md:text-6xl text-[var(--paper-2)] leading-[0.95]">
                                Analytics <span className="text-[var(--danger)]">en vivo</span>
                            </h1>
                            <p className="font-mono text-xs text-[var(--ink-muted)] mt-2">
                                Sesión: {user?.email} · Tracking first-party · IPs hasheadas
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="inline-flex border border-[var(--line)] bg-[#0d0d0d]" data-testid="range-switcher">
                                {RANGES.map((r) => (
                                    <button
                                        key={r.days}
                                        onClick={() => setDays(r.days)}
                                        data-testid={`range-${r.days}`}
                                        className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                                            days === r.days
                                                ? "bg-[var(--danger)] text-white"
                                                : "text-[var(--paper)] hover:text-[var(--warning)]"
                                        }`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={reload}
                                className="btn-brutal btn-brutal-ghost"
                                disabled={loading}
                                data-testid="analytics-reload"
                            >
                                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                                Recargar
                            </button>
                            <button onClick={handleLogout} className="btn-brutal btn-brutal-ghost">
                                <LogOut className="w-4 h-4" />
                                Salir
                            </button>
                        </div>
                    </div>

                    {/* KPI grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="kpi-grid-analytics">
                        <Kpi icon={Eye} label="Page views" value={fmt(overview?.page_views)} color="text-[var(--paper-2)]" />
                        <Kpi icon={Users} label="Sesiones únicas" value={fmt(overview?.sessions)} color="text-[var(--paper-2)]" />
                        <Kpi
                            icon={Download}
                            label="Descargas totales"
                            value={fmt(overview?.downloads_total)}
                            color="text-[var(--warning)]"
                        />
                        <Kpi
                            icon={CircleDollarSign}
                            label="Aportes"
                            value={overview?.revenue_label || "—"}
                            color="text-[var(--danger)]"
                        />
                    </div>

                    {/* Secondary KPIs */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                        <Mini icon={Compass} label="Mapa Inverso iniciados" value={fmt(overview?.wizard_started)} />
                        <Mini icon={Compass} label="Mapa Inverso completados" value={fmt(overview?.wizard_completed)} />
                        <Mini
                            icon={TrendingUp}
                            label="% que aporta"
                            value={overview ? `${overview.contribution_rate_pct || 0}%` : "—"}
                        />
                        <Mini
                            icon={Heart}
                            label={`A SinAdicciones (${overview?.social_percent ?? 80}%)`}
                            value={overview?.to_sinadicciones_label || "—"}
                        />
                    </div>

                    {/* Charts row */}
                    <div className="grid lg:grid-cols-3 gap-6 mb-12">
                        {/* Timeseries */}
                        <div className="lg:col-span-2 dossier" data-testid="chart-timeseries">
                            <div className="dossier-header">
                                <span>Serie temporal · {days} días</span>
                                <span className="text-[var(--warning)]">Vistas · Sesiones · Pagos</span>
                            </div>
                            <div className="p-5 h-80">
                                {series.length === 0 ? (
                                    <EmptyChart loading={loading} />
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={series} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                            <XAxis dataKey="day" tick={{ fill: "#7a7167", fontSize: 10 }} />
                                            <YAxis tick={{ fill: "#7a7167", fontSize: 10 }} />
                                            <Tooltip
                                                contentStyle={{
                                                    background: "#0d0d0d",
                                                    border: "1px solid #2a2a2a",
                                                    fontFamily: "IBM Plex Mono, monospace",
                                                    fontSize: 12,
                                                }}
                                            />
                                            <Line type="monotone" dataKey="views" stroke="#E8E6E1" strokeWidth={2} dot={false} name="Views" />
                                            <Line type="monotone" dataKey="sessions" stroke="#FFB300" strokeWidth={2} dot={false} name="Sesiones" />
                                            <Line type="monotone" dataKey="orders" stroke="#D32F2F" strokeWidth={2} dot={false} name="Pagos" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>

                        {/* Funnel */}
                        <div className="dossier" data-testid="chart-funnel">
                            <div className="dossier-header">
                                <span>Embudo de conversión</span>
                                <span className="text-[var(--danger)]">EMI</span>
                            </div>
                            <div className="p-5 space-y-3">
                                {funnel.length === 0 ? (
                                    <EmptyChart loading={loading} />
                                ) : (
                                    funnel.map((s) => {
                                        const w = Math.max(2, Math.round((s.count / maxFunnel) * 100));
                                        return (
                                            <div key={s.key} data-testid={`funnel-${s.key}`}>
                                                <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.18em] mb-1">
                                                    <span className="text-[var(--paper)]">{s.label}</span>
                                                    <span className="text-[var(--warning)]">{fmt(s.count)} · {s.pct_of_top}%</span>
                                                </div>
                                                <div className="h-3 bg-[#0d0d0d] border border-[var(--line)]">
                                                    <div
                                                        className="h-full bg-[var(--danger)]"
                                                        style={{ width: `${w}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Email + Devices */}
                    <div className="grid lg:grid-cols-3 gap-6 mb-12">
                        <div className="dossier lg:col-span-2" data-testid="chart-email">
                            <div className="dossier-header">
                                <span>Email — entregables</span>
                                <span className="text-[var(--warning)]">Resend</span>
                            </div>
                            <div className="p-5">
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                                    <EmailKpi icon={Mail} label="Enviados" value={overview?.email?.sent} />
                                    <EmailKpi icon={MailCheck} label="Entregados" value={overview?.email?.delivered} />
                                    <EmailKpi icon={MailOpen} label="Abiertos" value={overview?.email?.opened} />
                                    <EmailKpi icon={MousePointerClick} label="Clicks" value={overview?.email?.clicked} />
                                    <EmailKpi icon={Mail} label="Rebotes" value={overview?.email?.bounced} />
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-center font-mono">
                                    <Rate label="Delivery" value={`${overview?.email?.delivery_rate_pct ?? 0}%`} />
                                    <Rate label="Apertura" value={`${overview?.email?.open_rate_pct ?? 0}%`} />
                                    <Rate label="Click" value={`${overview?.email?.click_rate_pct ?? 0}%`} />
                                </div>
                            </div>
                        </div>

                        <div className="dossier" data-testid="chart-devices">
                            <div className="dossier-header">
                                <span>Dispositivos</span>
                                <span className="text-[var(--ink-muted)]">{days}d</span>
                            </div>
                            <div className="p-5 h-56">
                                {(top.devices || []).length === 0 ? (
                                    <EmptyChart loading={loading} />
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={top.devices} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                            <XAxis dataKey="device" tick={{ fill: "#7a7167", fontSize: 10 }} />
                                            <YAxis tick={{ fill: "#7a7167", fontSize: 10 }} />
                                            <Tooltip
                                                contentStyle={{
                                                    background: "#0d0d0d",
                                                    border: "1px solid #2a2a2a",
                                                    fontFamily: "IBM Plex Mono, monospace",
                                                    fontSize: 12,
                                                }}
                                            />
                                            <Bar dataKey="count" fill="#FFB300" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Top pages + referrers */}
                    <div className="grid lg:grid-cols-2 gap-6 mb-12">
                        <div className="dossier" data-testid="top-pages">
                            <div className="dossier-header">
                                <span>Top páginas</span>
                                <span className="text-[var(--ink-muted)]">por visitas</span>
                            </div>
                            <div className="p-2 max-h-96 overflow-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)] border-b border-[var(--line)]">
                                            <th className="text-left p-3">Página</th>
                                            <th className="text-right p-3">Vistas</th>
                                            <th className="text-right p-3">Sesiones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(top.pages || []).length === 0 ? (
                                            <tr><td colSpan={3} className="p-6 text-center font-mono text-xs text-[var(--ink-muted)]">{loading ? "Cargando…" : "Sin datos aún."}</td></tr>
                                        ) : top.pages.map((p) => (
                                            <tr key={p.page} className="border-b border-dashed border-[var(--ink-dim)] hover:bg-[#1a1a1a]">
                                                <td className="p-3 font-mono text-xs text-[var(--paper-2)] break-all">{p.page || "/"}</td>
                                                <td className="p-3 font-mono text-xs text-right text-[var(--warning)]">{fmt(p.views)}</td>
                                                <td className="p-3 font-mono text-xs text-right text-[var(--paper)]">{fmt(p.sessions)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="dossier" data-testid="top-referrers">
                            <div className="dossier-header">
                                <span>Fuentes de tráfico</span>
                                <span className="text-[var(--ink-muted)]">referrers</span>
                            </div>
                            <div className="p-2 max-h-96 overflow-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)] border-b border-[var(--line)]">
                                            <th className="text-left p-3">Origen</th>
                                            <th className="text-right p-3">Hits</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(top.referrers || []).length === 0 ? (
                                            <tr><td colSpan={2} className="p-6 text-center font-mono text-xs text-[var(--ink-muted)]">{loading ? "Cargando…" : "Sin referrers todavía."}</td></tr>
                                        ) : top.referrers.map((r) => (
                                            <tr key={r.referrer} className="border-b border-dashed border-[var(--ink-dim)] hover:bg-[#1a1a1a]">
                                                <td className="p-3 font-mono text-xs text-[var(--paper-2)] break-all flex items-center gap-2">
                                                    <Globe className="w-3 h-3 text-[var(--ink-muted)] flex-shrink-0" />
                                                    {shortHost(r.referrer)}
                                                </td>
                                                <td className="p-3 font-mono text-xs text-right text-[var(--warning)]">{fmt(r.count)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Recent events */}
                    <div className="dossier" data-testid="recent-events">
                        <div className="dossier-header">
                            <span>Eventos recientes</span>
                            <span className="text-[var(--danger)] inline-flex items-center gap-2">
                                <Activity className="w-3 h-3" />
                                Stream
                            </span>
                        </div>
                        <div className="max-h-96 overflow-auto">
                            <table className="w-full">
                                <thead className="bg-[var(--paper-2)] text-black sticky top-0">
                                    <tr className="font-mono text-[10px] uppercase tracking-[0.2em]">
                                        <th className="text-left p-3">Cuándo</th>
                                        <th className="text-left p-3">Evento</th>
                                        <th className="text-left p-3">Página</th>
                                        <th className="text-left p-3">Sesión</th>
                                        <th className="text-left p-3">Disp.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recent.length === 0 ? (
                                        <tr><td colSpan={5} className="p-6 text-center font-mono text-xs text-[var(--ink-muted)]">{loading ? "Cargando eventos…" : "Sin eventos aún."}</td></tr>
                                    ) : recent.map((e) => (
                                        <tr key={e.id} className="border-t border-dashed border-[var(--ink-dim)] hover:bg-[#1a1a1a]" data-testid={`event-row-${e.id}`}>
                                            <td className="p-3 font-mono text-[10px] text-[var(--ink-muted)] whitespace-nowrap">{(e.ts || "").slice(0, 19).replace("T", " ")}</td>
                                            <td className="p-3 font-mono text-xs text-[var(--warning)] uppercase tracking-[0.15em]">{e.name}</td>
                                            <td className="p-3 font-mono text-xs text-[var(--paper-2)] break-all">{e.page || "—"}</td>
                                            <td className="p-3 font-mono text-[10px] text-[var(--ink-muted)]">{(e.session_id || "").slice(0, 12)}…</td>
                                            <td className="p-3 font-mono text-[10px] text-[var(--paper)]">
                                                <span className="inline-flex items-center gap-1">
                                                    {e.device === "mobile" ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                                                    {e.browser || e.device || "—"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

function Kpi({ icon: Icon, label, value, color }) {
    return (
        <div className="border border-[var(--line)] bg-[#0d0d0d] p-6">
            <Icon className={`w-6 h-6 ${color} mb-4`} />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)] mb-2">{label}</p>
            <p className={`font-display text-5xl leading-none ${color}`}>{value ?? "—"}</p>
        </div>
    );
}

function Mini({ icon: Icon, label, value }) {
    return (
        <div className="border border-dashed border-[var(--ink-dim)] bg-[#0a0a0a] p-4">
            <div className="flex items-center justify-between mb-2">
                <Icon className="w-4 h-4 text-[var(--ink-muted)]" />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">{label}</p>
            </div>
            <p className="font-display text-2xl text-[var(--paper-2)]">{value ?? "—"}</p>
        </div>
    );
}

function EmailKpi({ icon: Icon, label, value }) {
    return (
        <div className="border border-[var(--line)] bg-[#0a0a0a] p-3 text-center">
            <Icon className="w-4 h-4 text-[var(--ink-muted)] mx-auto mb-1" />
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">{label}</p>
            <p className="font-display text-2xl text-[var(--paper-2)] mt-1">{fmt(value)}</p>
        </div>
    );
}

function Rate({ label, value }) {
    return (
        <div className="border-t border-[var(--line)] pt-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">{label}</p>
            <p className="text-2xl text-[var(--warning)] font-display">{value}</p>
        </div>
    );
}

function EmptyChart({ loading }) {
    return (
        <div className="h-full flex items-center justify-center text-center">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)]">
                {loading ? "Cargando…" : "Sin datos en este rango"}
            </p>
        </div>
    );
}

function fmt(n) {
    if (n === null || n === undefined) return "—";
    if (typeof n === "string") return n;
    return n.toLocaleString("es-CL");
}

function shortHost(url) {
    if (!url) return "—";
    try {
        const u = new URL(url);
        return u.hostname.replace(/^www\./, "");
    } catch {
        return url.slice(0, 40);
    }
}
